
import { AnimatePresence, motion } from 'framer-motion';
import jsPDF from 'jspdf';
import { Calendar, Download, MapPin, Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import userService from '../../services/userService';
import { getAllSites } from '../SiteManagement/siteApi';
import { fdocApi } from '../Tax/FdocApi';
import { Card } from '../ui';
import { invoiceApi } from './invoiceApi';

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

// Helper function to map API invoice to UI format
const mapInvoiceToUI = (apiInvoice, apiLines = []) => {
  const inv = apiInvoice || {};
  const notesParsed = parseInvoiceNotes(inv.notes);
  const paymentModeRaw = inv.payment_mode || inv.paymentMode;
  const paymentMode = (paymentModeRaw && paymentModeRaw !== 'N/A') ? paymentModeRaw : (notesParsed.paymentMode || '');
  const poNumber = inv.po_number || inv.poNumber || notesParsed.poNumber || '';
  const modeOfTransaction = inv.mode_of_transaction || inv.modeOfTransaction || notesParsed.modeOfTransaction || '';
  const transactionId = inv.transaction_id || inv.transactionId || notesParsed.transactionId || '';
  const amountPaidRaw = inv.amount_paid ?? inv.amountPaid ?? notesParsed.amountPaid;
  const amountPaid = amountPaidRaw != null && amountPaidRaw !== '' ? (typeof amountPaidRaw === 'number' ? amountPaidRaw : parseFloat(String(amountPaidRaw).replace(/[₹,\s]/g, ''))) : undefined;
  return {
    id: inv.invoice_id,
    invoiceNumber: inv.invoice_number,
    date: inv.invoice_date,
    client: inv.customer_name || 'Unknown Customer',
    customerId: inv.customer_id,
    siteId: inv.site_id || null,
    siteName: inv.site_name || 'N/A',
    billingAddress: inv.billing_address
      ? (typeof inv.billing_address === 'string' ? inv.billing_address : `${inv.billing_address.street || ''}, ${inv.billing_address.city || ''}, ${inv.billing_address.state || ''} ${inv.billing_address.pincode || ''}`)
      : 'N/A',
    shippingAddress: inv.shipping_address
      ? (typeof inv.shipping_address === 'string' ? inv.shipping_address : `${inv.shipping_address.street || ''}, ${inv.shipping_address.city || ''}, ${inv.shipping_address.state || ''} ${inv.shipping_address.pincode || ''}`)
      : 'N/A',
    poNumber: poNumber || undefined,
    paymentMode: paymentMode || undefined,
    modeOfTransaction: modeOfTransaction || undefined,
    transactionId: transactionId || undefined,
    amountPaid,
    paymentTerms: inv.payment_terms || inv.paymentTerms || 'N/A',
    lineItems: (apiLines || []).map(line => ({
      name: line.description || '',
      quantity: line.quantity ?? 0,
      units: line.unit || '',
      rate: line.unit_price ?? 0,
      amount: line.line_total ?? line.line_subtotal ?? 0,
    })),
    subtotal: inv.subtotal ?? 0,
    discount: inv.discount ?? 0,
    shippingHandling: inv.shipping_total ?? inv.shipping_handling ?? 0,
    insurance: inv.insurance ?? 0,
    totalTax: inv.tax_total ?? inv.tax_amount ?? 0,
    grandTotal: inv.grand_total ?? inv.total_amount ?? 0,
    status: inv.status === 'paid' ? 'Paid' : 
            inv.status === 'overdue' ? 'Overdue' : 
            inv.status === 'sent' ? 'Sent' : 
            inv.status === 'cancelled' ? 'Cancelled' : 'Draft',
    paymentStatus: inv.payment_status || 'unpaid',
    dueDate: inv.due_date,
    currency: inv.currency || 'INR',
    notes: inv.notes || '',
    termsOfSale: inv.terms_of_sale ?? inv.termsOfSale ?? '',
    // POC for delivery: API may use shipping_details.contact, poc_client, or flat poc_*
    pocName: inv.shipping_details?.contact?.name ?? inv.poc_client?.name ?? inv.poc_name ?? inv.pocName ?? inv.billing_contact ?? inv.contact_name ?? '',
    pocPhone: inv.shipping_details?.contact?.phone ?? inv.shipping_details?.contact?.mobile ?? inv.shipping_details?.contact?.mobile_no ?? inv.poc_client?.phone ?? inv.poc_client?.mobile ?? inv.poc_phone ?? inv.pocPhone ?? inv.billing_phone ?? inv.contact_phone ?? '',
    pocRole: inv.shipping_details?.contact?.role ?? inv.poc_client?.role ?? inv.poc_role ?? inv.pocRole ?? '',
    pocEmail: inv.shipping_details?.contact?.email ?? inv.poc_client?.email ?? inv.poc_email ?? inv.pocEmail ?? inv.billing_email ?? inv.contact_email ?? '',
    // Shipping details: API may use shipping_details object or flat fields
    shippingPackages: inv.shipping_details?.packages ?? inv.shipping_details?.no_of_packages ?? inv.shipping_packages ?? inv.shippingPackages ?? '',
    grossQuantity: inv.shipping_details?.gross_quantity ?? inv.shipping_details?.est_gross_quantity ?? inv.gross_quantity ?? inv.grossQuantity ?? '',
    netQuantity: inv.shipping_details?.net_quantity ?? inv.shipping_details?.est_net_quantity ?? inv.net_quantity ?? inv.netQuantity ?? '',
    transportationMode: inv.shipping_details?.mode_of_transportation ?? inv.shipping_details?.transportation_mode ?? inv.transportation_mode ?? inv.transportationMode ?? '',
    transportationTerms: inv.shipping_details?.transportation_terms ?? inv.transportation_terms ?? inv.transportationTerms ?? '',
    // Discount (type and value for Create Invoice form)
    discountType: inv.discount_type ?? inv.discountType ?? 'percentage',
    discountValue: inv.discount_value ?? inv.discountValue ?? (inv.discount != null ? String(inv.discount) : ''),
    // Preserve FDOC reference if present
    fdoc_id: inv.fdoc_id,
    fdoc_document: inv.fdoc_document,
    version_no: inv.version_no ?? inv.versionNo ?? null,
    versionNo: inv.version_no ?? inv.versionNo ?? null,
  };
};

const resolveSiteValue = (site) => {
  return site?.site_id ?? site?.siteId ?? site?.id ?? site?._id ?? site?.site_code ?? site?.siteCode ?? '';
};

/** Keys that are shown in Payment and transaction / Invoice info – strip from notes display */
const NOTE_KEYS_TO_STRIP = [
  'po number', 'po date', 'payment mode', 'insurance',
  'mode of transaction', 'transaction id', 'amount paid',
  'poc name', 'poc phone', 'poc role', 'poc email',
  'contact person', 'contact name', 'contact phone', 'contact email',
];

/** Return notes text with payment/transaction (and PO, POC) lines removed for display */
const getNotesForDisplay = (notesText = '') => {
  const text = String(notesText).trim();
  if (!text) return '';
  const lines = text.split(/\r?\n/);
  const kept = lines.filter((line) => {
    const m = line.match(/^([^:]+):/);
    if (!m) return true;
    const key = m[1].trim().toLowerCase();
    return !NOTE_KEYS_TO_STRIP.includes(key);
  });
  return kept.join('\n').trim();
};

/** Split notes so Terms of Sale never appears in Special Notes – two separate boxes */
const splitNotesAndTerms = (notesStr, explicitTerms = '') => {
  const notes = (notesStr && String(notesStr).trim()) || '';
  const termsGiven = explicitTerms != null && String(explicitTerms).trim() !== '';
  if (termsGiven) {
    const terms = String(explicitTerms).trim();
    let special = notes;
    if (terms && notes.includes(terms)) {
      special = notes.replace(terms, '').replace(/\n\n+/g, '\n').trim();
    }
    return { specialNotes: special, termsOfSale: terms };
  }
  if (!notes) return { specialNotes: '', termsOfSale: '' };
  const termsMatch = notes.match(/\bTerms\s+of\s+Sale\s*:?\s*\n?([\s\S]*?)(?=\n\n|\n[A-Z]|\z)/i) || notes.match(/\bTerms\s+of\s+Sale\s*:?\s*([^\n]*)/i);
  if (termsMatch) {
    const terms = termsMatch[1].trim();
    const special = notes.replace(/\bTerms\s+of\s+Sale\s*:?\s*\n?[\s\S]*/i, '').trim();
    return { specialNotes: special, termsOfSale: terms };
  }
  const lines = notes.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length >= 2) {
    return { specialNotes: lines[0], termsOfSale: lines.slice(1).join('\n') };
  }
  if (lines.length === 1) return { specialNotes: lines[0], termsOfSale: '' };
  return { specialNotes: notes, termsOfSale: '' };
};

/** Parse notes text for "PO Number: X", "Payment Mode: X", "Mode of transaction: X" */
const parseInvoiceNotes = (notesText = '') => {
  const text = String(notesText).trim();
  if (!text) return {};
  const result = {};
  const lines = text.split(/\r?\n/);
  const keyMap = {
    'po number': 'poNumber',
    'payment mode': 'paymentMode',
    'mode of transaction': 'modeOfTransaction',
    'transaction id': 'transactionId',
    'amount paid': 'amountPaid',
  };
  for (const line of lines) {
    const match = line.match(/^([^:]+):\s*(.*)$/);
    if (!match) continue;
    const key = match[1].trim().toLowerCase();
    const value = match[2].trim();
    const field = keyMap[key];
    if (field && value) {
      if (field === 'amountPaid') result[field] = value.replace(/[₹,\s]/g, '').trim();
      else result[field] = value;
    }
  }
  return result;
};

// Resolve display site name from app's sites list so it matches the filtered site (not API's site_name)
const getDisplaySiteName = (invoice, sites) => {
  if (!invoice || !sites?.length || !invoice.siteId) return invoice?.siteName ?? 'N/A';
  const site = sites.find(s => resolveSiteValue(s) === invoice.siteId);
  return site ? (site.site_name || site.name || invoice.siteName) : (invoice.siteName ?? 'N/A');
};

const InvoiceView = ({ 
  externalSite = null, 
  externalMonth = null, 
  externalSites = null,
  hideSelectors = false,
  fetchFromFdoc = false,
  fdocType = '',
  esignedOnly = false,
}) => {
  // Log screen view
  useScreenLogging('InvoiceView', {
    component: 'InvoiceView',
    hasExternalSite: !!externalSite,
    fetchFromFdoc,
  });
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedSite, setSelectedSite] = useState(externalSite || null);
  const [selectedMonth, setSelectedMonth] = useState(externalMonth || getCurrentMonth());
  const [selectedCard, setSelectedCard] = useState(null);
  const [sites, setSites] = useState(externalSites || []);
  const [isSitesLoading, setIsSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [invoiceDetails, setInvoiceDetails] = useState({}); // Cache for full invoice details
  const [refreshKey, setRefreshKey] = useState(0); // Key to force refresh
  const [invoiceIdFilter, setInvoiceIdFilter] = useState('');
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloadUrlExpiresIn, setDownloadUrlExpiresIn] = useState(null);
  const [documentContentType, setDocumentContentType] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // Handle location state from navigation (e.g., after creating an invoice)
  useEffect(() => {
    if (location.state) {
      const { siteId, site, month, invoiceId, refresh } = location.state;
      
      // Auto-select site if provided
      if (siteId && sites.length > 0) {
        const siteToSelect = sites.find(s => 
          s.site_id === siteId || s.id === siteId || s.siteCode === siteId
        );
        if (siteToSelect) {
          setSelectedSite(siteToSelect);
        }
      } else if (site) {
        setSelectedSite(site);
      }
      
      // Auto-select month if provided
      if (month) {
        setSelectedMonth(month);
      }
      
      // Trigger refresh if indicated
      if (refresh) {
        setRefreshKey(prev => prev + 1);
      }
      
      // Clear location state to prevent re-triggering on re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state, sites]);

  // Update internal state when external props change
  useEffect(() => {
    if (externalSite !== null) {
      setSelectedSite(externalSite);
    }
  }, [externalSite]);

  useEffect(() => {
    if (externalMonth !== null) {
      setSelectedMonth(externalMonth);
    }
  }, [externalMonth]);

  useEffect(() => {
    if (externalSites !== null && externalSites.length > 0) {
      setSites(externalSites);
    }
  }, [externalSites]);

  // Fetch sites only if not provided externally
  useEffect(() => {
    if (externalSites !== null) {
      // Sites are provided externally, don't fetch
      return;
    }

    let isMounted = true;
    const fetchSites = async () => {
      setIsSitesLoading(true);
      setSitesError('');
      try {
        const response = await getAllSites();
        const list = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
            ? response
            : [];
        const sorted = [...list].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
        );
        if (isMounted) {
          setSites(sorted);
        }
      } catch (error) {
        if (isMounted) {
          setSitesError(error?.message || 'Unable to load sites. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setIsSitesLoading(false);
        }
      }
    };
    fetchSites();
    return () => {
      isMounted = false;
    };
  }, [externalSites]);

  // Fetch invoices when site and month change
  useEffect(() => {
    if (!selectedSite) {
      setInvoices([]);
      return;
    }

    let isMounted = true;

    const fetchInvoices = async () => {
      setIsLoading(true);
      setError('');

      try {
        const [year, month] = selectedMonth.split('-');
        const startDate = `${year}-${month}-01`;
        const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
        const endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;

        const siteId = resolveSiteValue(selectedSite);

        // If fetchFromFdoc is true, fetch from FDOC API instead of invoice API
        if (fetchFromFdoc) {
          try {
            console.log('InvoiceView: Fetching invoices from FDOC (Invoice OO)');
            const fdocParams = { limit: 500 };
            if (esignedOnly) {
              fdocParams.esign_status = 'applied';
            }
            const fdocResponse = await fdocApi.listDocuments(fdocParams);
            
            // Process FDOC documents to find Invoice types
            const fdocBatchSize = 10;
            const fdocInvoices = [];
            
            for (let i = 0; i < fdocResponse.items.length; i += fdocBatchSize) {
              const batch = fdocResponse.items.slice(i, i + fdocBatchSize);
              const batchResults = await Promise.allSettled(
                batch.map(async (doc) => {
                  try {
                    const fullDoc = await fdocApi.getDocument(doc.fdoc_id);
                    const extractedData = fullDoc.extracted_data;
                    let docType = extractedData?.type || 'UNKNOWN';
                    
                    // Infer type from file name if needed
                    if (docType === 'UNKNOWN' || !docType) {
                      const fileName = (fullDoc?.file_name || doc?.file_name || '').toLowerCase();
                      const fileKey = (fullDoc?.s3_key || doc?.s3_key || '').toLowerCase();
                      const combinedText = `${fileName} ${fileKey}`;
                      
                      if (combinedText.includes('invoice') || combinedText.includes('inv')) {
                        docType = 'Invoice';
                      }
                    }
                    
                    // Normalize Invoice types
                    const normalizedType = docType.toLowerCase();
                    if (normalizedType === 'invoice' || normalizedType === 'invoices') {
                      // Get site_id from document
                      const docSiteId = fullDoc?.site_id || doc.site_id || fullDoc?.siteId || doc.siteId || null;
                      
                      // Check site_id match if site filter is applied
                      if (siteId) {
                        if (!docSiteId || docSiteId !== siteId) {
                          return null; // Skip if site doesn't match
                        }
                      }
                      
                      // Check date range if provided
                      const docDate = doc.uploaded_at ? new Date(doc.uploaded_at).toISOString().split('T')[0] : null;
                      if (docDate) {
                        if (startDate && docDate < startDate) {
                          return null;
                        }
                        if (endDate && docDate > endDate) {
                          return null;
                        }
                      }
                      
                      // Transform FDOC document to invoice format
                      const extractedFields = extractedData?.fields || {};
                      const invoiceNumber = extractedFields.invoice_number || extractedFields.invoiceNumber || `FDOC-${doc.fdoc_id.substring(0, 8).toUpperCase()}`;
                      const invoiceDate = extractedFields.invoice_date || extractedFields.date || docDate || new Date().toISOString().split('T')[0];
                      
                      return {
                        invoice_id: doc.fdoc_id,
                        invoice_number: invoiceNumber,
                        invoice_date: invoiceDate,
                        customer_name: extractedFields.customer_name || extractedFields.customerName || 'Unknown Customer',
                        site_id: docSiteId,
                        site_name: fullDoc?.site_name || 'N/A',
                        billing_address: extractedFields.billing_address || {},
                        shipping_address: extractedFields.shipping_address || {},
                        payment_terms: extractedFields.payment_terms || 'N/A',
                        payment_mode: extractedFields.payment_mode || extractedFields.paymentMode || '',
                        po_number: extractedFields.po_number || extractedFields.poNumber || '',
                        mode_of_transaction: extractedFields.mode_of_transaction || extractedFields.modeOfTransaction || '',
                        notes: extractedFields.notes || '',
                        subtotal: parseFloat(extractedFields.subtotal || extractedFields.sub_total || 0),
                        tax_total: parseFloat(extractedFields.tax_total || extractedFields.tax_amount || 0),
                        grand_total: parseFloat(extractedFields.grand_total || extractedFields.total_amount || 0),
                        status: 'sent',
                        payment_status: 'unpaid',
                        currency: extractedFields.currency || 'INR',
                        version_no:
                          fullDoc?.version_no ??
                          fullDoc?.versionNo ??
                          doc?.version_no ??
                          doc?.versionNo ??
                          null,
                        // Store FDOC reference
                        fdoc_id: doc.fdoc_id,
                        fdoc_document: fullDoc,
                      };
                    }
                    return null;
                  } catch (err) {
                    console.warn(`InvoiceView: Failed to process FDOC document ${doc.fdoc_id}:`, err.message);
                    return null;
                  }
                })
              );
              
              batchResults.forEach((result) => {
                if (result.status === 'fulfilled' && result.value) {
                  fdocInvoices.push(result.value);
                }
              });
            }
            
            // Map FDOC invoices to UI format
          const mappedInvoices = fdocInvoices.map(inv => {
            const mapped = mapInvoiceToUI(inv, []); // Lines will be fetched on demand when card is selected
            // Store FDOC reference for later use
            mapped.fdoc_id = inv.fdoc_id;
            mapped.fdoc_document = inv.fdoc_document;
            const v = inv.version_no ?? inv.versionNo ?? null;
            mapped.version_no = v;
            mapped.versionNo = v;
            return mapped;
          });

          // Resolve site name from app's sites list so displayed name matches filtered site
          if (isMounted && sites.length > 0) {
            mappedInvoices.forEach(inv => {
              if (inv.siteId) {
                const site = sites.find(s => resolveSiteValue(s) === inv.siteId);
                if (site) inv.siteName = site.site_name || site.name || inv.siteName;
              }
            });
          }

          if (isMounted) {
            setInvoices(mappedInvoices);
          }
          } catch (fdocErr) {
            console.error('InvoiceView: Error fetching invoices from FDOC:', fdocErr);
            if (isMounted) {
              setError(fdocErr.message || 'Failed to fetch invoices from FDOC');
              setInvoices([]);
            }
          } finally {
            if (isMounted) {
              setIsLoading(false);
            }
          }
          return;
        }

        // Regular invoice API fetch
        const params = {
          site_id: siteId, // Pass site_id to API for server-side filtering
          start_date: startDate,
          end_date: endDate,
          limit: 100,
        };

        // Add invoice number filter if provided
        if (invoiceIdFilter.trim()) {
          params.invoice_number_contains = invoiceIdFilter.trim();
        }

        const result = await invoiceApi.listInvoices(params);

        if (result.success) {
          // API now filters by site_id, but we still filter client-side as fallback
          // for invoices that might not have site_id set (backward compatibility)
          let filtered = result.invoices || [];
          
          // Client-side fallback: If API didn't filter properly or invoice doesn't have site_id,
          // filter by site_id if available in invoice object
          if (siteId && filtered.length > 0) {
            filtered = filtered.filter(inv => {
              const invSiteId = inv.site_id || inv.siteId || null;
              // Include invoice if it matches site_id or doesn't have site_id (backward compatibility)
              return !invSiteId || invSiteId === siteId;
            });
          }
          
          const mappedInvoices = filtered.map(inv => {
            // Use cached details if available, otherwise create basic mapping
            return mapInvoiceToUI(inv, []); // Lines will be fetched on demand when card is selected
          });

          // Resolve site name from app's sites list so displayed name matches filtered site
          if (isMounted && sites.length > 0) {
            mappedInvoices.forEach(inv => {
              if (inv.siteId) {
                const site = sites.find(s => resolveSiteValue(s) === inv.siteId);
                if (site) inv.siteName = site.site_name || site.name || inv.siteName;
              }
            });
          }

          if (isMounted) {
            setInvoices(mappedInvoices);
          }
        } else {
          if (isMounted) {
            setError(result.error || 'Failed to fetch invoices');
            setInvoices([]);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Failed to fetch invoices');
          setInvoices([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchInvoices();

    return () => {
      isMounted = false;
    };
  }, [selectedSite, selectedMonth, refreshKey, fetchFromFdoc, invoiceIdFilter, sites, esignedOnly]);

  // Fetch full invoice details when a card is selected
  useEffect(() => {
    if (!selectedCard || !selectedCard.id) {
      setDownloadUrl(null);
      setDownloadUrlExpiresIn(null);
      setDocumentContentType(null);
      return;
    }

    // Reset document states when card changes
    setDownloadUrl(null);
    setDownloadUrlExpiresIn(null);
    setDocumentContentType(null);

    // Check if this is an FDOC document (OO invoice)
    if (selectedCard.fdoc_id || fetchFromFdoc) {
      setIsLoadingDetails(true);
      const fetchFdocDocument = async () => {
        try {
          const fdocId = selectedCard.fdoc_id;
          if (!fdocId) {
            console.warn('InvoiceView: No fdoc_id found on selected card. Card data:', selectedCard);
            setIsLoadingDetails(false);
            return;
          }

          console.log('InvoiceView: Fetching FDOC document:', fdocId);
          const fullDoc = await fdocApi.getDocument(fdocId);
          console.log('FDOC Document fetched:', fullDoc);

          // Try to find a direct download URL first
          const downloadUrlFromDoc = fullDoc?.download_url || fullDoc?.file_url || fullDoc?.s3_url || fullDoc?.presigned_url || fullDoc?.view_url;
          if (downloadUrlFromDoc && (downloadUrlFromDoc.startsWith('http://') || downloadUrlFromDoc.startsWith('https://'))) {
            setDownloadUrl(downloadUrlFromDoc);
            setDocumentContentType(fullDoc?.content_type || fullDoc?.file_content_type || 'application/pdf');
          } else {
            // Get S3 key from document - try keys in priority order
            // Build list of available keys in priority order
            const availableKeys = [
              fullDoc?.s3_key_permanent,
              fullDoc?.s3_key,
              fullDoc?.s3_key_temp,
              fullDoc?.object_key
            ].filter(key => key && key.trim() !== '');
            
            console.log('InvoiceView: Available S3 keys:', {
              s3_key_permanent: fullDoc?.s3_key_permanent,
              s3_key: fullDoc?.s3_key,
              s3_key_temp: fullDoc?.s3_key_temp,
              object_key: fullDoc?.object_key,
              availableKeys
            });
            
            if (availableKeys.length === 0) {
              console.warn('InvoiceView: No S3 key or download URL found in FDOC document');
              return;
            }
            
            // Try each key in order until one works
            const bucket = fullDoc?.bucket || 'innoworx-fdoc-permanent';
            let presignedUrl = null;
            
            for (const s3Key of availableKeys) {
              try {
                // Check if it's already a URL
                if (s3Key.startsWith('http://') || s3Key.startsWith('https://')) {
                  console.log('InvoiceView: Using direct URL:', s3Key);
                  setDownloadUrl(s3Key);
                  setDocumentContentType(fullDoc?.content_type || fullDoc?.file_content_type || 'application/pdf');
                  return;
                }
                
                // Convert S3 key to S3 path format
                const s3Path = `s3://${bucket}/${s3Key}`;
                console.log('InvoiceView: Trying S3 path:', s3Path);
                
                // Get presigned URL from S3 path
                presignedUrl = await userService.getS3PresignedUrl(s3Path);
                if (presignedUrl) {
                  // Check if URL has wrong region (this will cause 400/403 errors)
                  if (presignedUrl.includes('ap-south-1')) {
                    console.error(
                      '❌ CRITICAL: Presigned URL region mismatch.\n' +
                      'The S3 bucket (innoworx-fdoc-permanent) is in us-east-1, but the presigned URL is generated with ap-south-1.\n' +
                      'This will cause 400/403 errors. Backend fix required: The /s3-presigned endpoint must detect the bucket region and generate URLs with us-east-1.'
                    );
                  }
                  console.log('InvoiceView: Successfully got presigned URL for key:', s3Key);
                  setDownloadUrl(presignedUrl);
                  setDocumentContentType(fullDoc?.content_type || fullDoc?.file_content_type || fullDoc?.file_type ? `application/${fullDoc.file_type}` : 'application/pdf');
                  return; // Success, exit loop
                }
              } catch (error) {
                console.warn(`InvoiceView: Failed to get presigned URL for key "${s3Key}":`, error);
                // Continue to next key
                continue;
              }
            }
            
            // If we get here, all keys failed
            console.error('InvoiceView: All S3 keys failed to generate presigned URLs');
          }
          
          // Do NOT set invoice details for FDOC documents to hide the preview
        } catch (err) {
          // Handle 500 errors and other fetch failures gracefully
          const errorMessage = err.message?.toLowerCase();
          if (err.status === 500 || errorMessage?.includes('500') || errorMessage?.includes('internal server error') || errorMessage?.includes('server error')) {
            console.error(`InvoiceView: Document ${selectedCard.fdoc_id} is not accessible (server error). Silently skipping.`);
          } else {
            console.error('Error fetching FDOC document:', err);
          }
        } finally {
          setIsLoadingDetails(false);
        }
      };

      fetchFdocDocument();
      return;
    }

    // For non-FDOC invoices, fetch details normally
    // Check if we already have the details
    if (invoiceDetails[selectedCard.id] && invoiceDetails[selectedCard.id].lineItems?.length > 0) {
      return;
    }

    let isMounted = true;

    const fetchInvoiceDetails = async () => {
      try {
        // Fetch from regular invoice API
        const result = await invoiceApi.getInvoice(selectedCard.id);
        if (result.success && isMounted) {
          const mapped = mapInvoiceToUI(result.invoice, result.lines);
          setInvoiceDetails(prev => ({
            ...prev,
            [selectedCard.id]: mapped,
          }));
          setSelectedCard(mapped);
        }
      } catch (err) {
        console.error('Error fetching invoice details:', err);
      }
    };

    fetchInvoiceDetails();

    return () => {
      isMounted = false;
    };
  }, [selectedCard?.id]);

  // Filter invoices by site, month, and invoice ID
  const filteredInvoices = useMemo(() => {
    if (!invoiceIdFilter.trim()) {
      return invoices;
    }

    const filterLower = invoiceIdFilter.toLowerCase().trim();
    return invoices.filter(invoice => {
      const invoiceNumber = invoice.invoiceNumber || '';
      return invoiceNumber.toLowerCase().includes(filterLower);
    });
  }, [invoices, invoiceIdFilter]);

  // Calculate totals
  const totalAmount = useMemo(() => {
    return filteredInvoices.reduce((sum, invoice) => sum + invoice.grandTotal, 0);
  }, [filteredInvoices]);

  const formatCurrency = (amount = 0) =>
    Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);

  const handleDownload = (invoice) => {
    if (!invoice) return;

    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPosition = margin;
      const lineHeight = 7;
      const sectionSpacing = 5;

      const centerText = (text, fontSize = 12, isBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        const textWidth = pdf.getTextWidth(text);
        const xPosition = (pageWidth - textWidth) / 2;
        pdf.text(text, xPosition, yPosition);
        yPosition += lineHeight;
      };

      const addText = (text, fontSize = 10, isBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        pdf.text(text, margin, yPosition);
        yPosition += lineHeight;
      };

      const addTwoColumn = (leftText, rightText, fontSize = 10, leftBold = false, rightBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', leftBold ? 'bold' : 'normal');
        pdf.text(leftText, margin, yPosition);
        
        pdf.setFont('helvetica', rightBold ? 'bold' : 'normal');
        const rightTextWidth = pdf.getTextWidth(rightText);
        pdf.text(rightText, pageWidth - margin - rightTextWidth, yPosition);
        yPosition += lineHeight;
      };

      const drawLine = () => {
        pdf.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += sectionSpacing;
      };

      // Header
      centerText('INVOICE', 18, true);
      yPosition += 3;
      if (invoice.siteName) {
        centerText(invoice.siteName, 12, true);
        yPosition += 2;
      }
      drawLine();
      yPosition += sectionSpacing;

      // Invoice Information
      addText('INVOICE INFORMATION', 12, true);
      yPosition += 2;
      addTwoColumn('Invoice Number:', invoice.invoiceNumber || 'N/A', 10);
      addTwoColumn('Invoice Date:', invoice.date || 'N/A', 10);
      if (invoice.dueDate) {
        addTwoColumn('Due Date:', invoice.dueDate, 10);
      }
      if (invoice.paymentTerms) {
        addTwoColumn('Payment Terms:', invoice.paymentTerms, 10);
      }
      if (invoice.paymentStatus) {
        addTwoColumn('Payment Status:', invoice.paymentStatus, 10);
      }
      if (invoice.siteName) {
        addTwoColumn('Site:', invoice.siteName, 10);
      }
      if (invoice.currency) {
        addTwoColumn('Currency:', invoice.currency, 10);
      }
      addTwoColumn('Status:', invoice.status || 'N/A', 10);
      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing;

      // Client Information
      if (invoice.client || invoice.billingAddress) {
        addText('CLIENT INFORMATION', 12, true);
        yPosition += 2;
        if (invoice.client) {
          addTwoColumn('Client:', invoice.client, 10);
        }
        if (invoice.billingAddress) {
          const addrLines = pdf.splitTextToSize(invoice.billingAddress, 80);
          pdf.setFontSize(10);
          pdf.text('Billing Address:', margin, yPosition);
          yPosition += lineHeight;
          pdf.setFontSize(9);
          addrLines.forEach(line => {
            pdf.text(line, margin + 5, yPosition);
            yPosition += lineHeight - 1;
          });
        }
        if (invoice.shippingAddress && invoice.shippingAddress !== invoice.billingAddress) {
          yPosition += 2;
          pdf.setFontSize(10);
          pdf.text('Shipping Address:', margin, yPosition);
          yPosition += lineHeight;
          const shipLines = pdf.splitTextToSize(invoice.shippingAddress, 80);
          pdf.setFontSize(9);
          shipLines.forEach(line => {
            pdf.text(line, margin + 5, yPosition);
            yPosition += lineHeight - 1;
          });
        }
        yPosition += sectionSpacing;
        drawLine();
        yPosition += sectionSpacing;
      }

      // Items Header
      addText('LINE ITEMS', 12, true);
      yPosition += 3;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text('S.No', margin, yPosition);
      pdf.text('Name', margin + 20, yPosition);
      pdf.text('Qty', margin + 90, yPosition);
      pdf.text('Units', margin + 105, yPosition);
      pdf.text('Rate', margin + 125, yPosition);
      pdf.text('Amount', pageWidth - margin - 30, yPosition, { align: 'right' });
      yPosition += lineHeight;
      drawLine();
      yPosition += 2;

      // Items List
      if (invoice.lineItems && invoice.lineItems.length > 0) {
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        invoice.lineItems.forEach((item, index) => {
          if (yPosition > pageHeight - 40) {
            pdf.addPage();
            yPosition = margin;
          }

          const itemName = item.name || `Item ${index + 1}`;
          const itemLines = pdf.splitTextToSize(itemName, 60);
          const itemHeight = itemLines.length * 5;

          pdf.text((index + 1).toString(), margin, yPosition);
          pdf.text(itemLines, margin + 20, yPosition);
          pdf.text((item.quantity || 0).toString(), margin + 90, yPosition);
          pdf.text(item.units || 'N/A', margin + 105, yPosition);
          pdf.text(formatCurrency(item.rate || 0), margin + 125, yPosition);
          pdf.text(formatCurrency(item.amount || 0), pageWidth - margin, yPosition, { align: 'right' });
          yPosition += Math.max(itemHeight, lineHeight) + 2;
        });
      } else {
        pdf.setFontSize(9);
        pdf.text('No line items found', margin, yPosition);
        yPosition += lineHeight;
      }

      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing;

      if (yPosition > pageHeight - 60) {
        pdf.addPage();
        yPosition = margin;
      }

      // Summary
      addText('SUMMARY', 12, true);
      yPosition += 2;
      addTwoColumn('Subtotal:', formatCurrency(invoice.subtotal || 0), 10);
      if (invoice.discount > 0) {
        addTwoColumn('Discount:', formatCurrency(invoice.discount || 0), 10);
      }
      if (invoice.shippingHandling > 0) {
        addTwoColumn('Shipping & Handling:', formatCurrency(invoice.shippingHandling || 0), 10);
      }
      if (invoice.totalTax > 0) {
        addTwoColumn('Tax:', formatCurrency(invoice.totalTax || 0), 10);
      }
      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing;
      addTwoColumn('GRAND TOTAL:', formatCurrency(invoice.grandTotal || 0), 14, true, true);
      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing * 2;

      centerText('Thank you for your business!', 10);

      pdf.save(`invoice-${invoice.invoiceNumber || 'unknown'}-${invoice.date || 'unknown'}.pdf`);
    } catch (error) {
      console.error('Failed to generate invoice PDF', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'Pending':
      case 'Sent':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'Overdue':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto h-auto">
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          <div className="rounded-3xl">
            {/* Invoice ID Filter - Always visible, Site and Month Selection - Only show if not hidden */}
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-6">
              <div className="min-w-[200px] flex-1">
                <label htmlFor="invoice-id-filter" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                  <Search className="w-4 h-4" />
                  Filter by Invoice ID
                </label>
                <input
                  id="invoice-id-filter"
                  type="text"
                  placeholder="Enter Invoice ID or Invoice Number..."
                  value={invoiceIdFilter}
                  onChange={(event) => setInvoiceIdFilter(event.target.value)}
                  className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                />
              </div>

              {!hideSelectors && (
                <>
                <div className="min-w-[200px]">
                  <label htmlFor="invoice-site" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                    <MapPin className="w-4 h-4" />
                    Select Site
                  </label>
                  <select
                    id="invoice-site"
                    value={selectedSite ? resolveSiteValue(selectedSite) : ''}
                    onChange={(e) => {
                      const siteId = e.target.value;
                      const site = sites.find(s => resolveSiteValue(s) === siteId);
                      setSelectedSite(site || null);
                    }}
                    className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                    required
                    disabled={isSitesLoading}
                  >
                    <option value="">Select a site</option>
                    {sites.map((site) => (
                      <option key={resolveSiteValue(site)} value={resolveSiteValue(site)}>
                        {site.site_name || site.name || 'Unknown Site'}
                      </option>
                    ))}
                  </select>
                  {sitesError && (
                    <p className="mt-1 text-xs text-red-600">{sitesError}</p>
                  )}
                </div>

                <div className="min-w-[200px]">
                  <label htmlFor="invoice-month" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                    <Calendar className="w-4 h-4" />
                    Select Month
                  </label>
                  <input
                    id="invoice-month"
                    type="month"
                    value={selectedMonth}
                    onChange={(event) => setSelectedMonth(event.target.value)}
                    className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                  />
                </div>
                </>
              )}
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="text-center py-12">
                <p className="text-gray-500">Loading invoices...</p>
              </div>
            )}

            {/* Error State */}
            {error && !isLoading && (
              <div className="text-center py-12">
                <p className="text-red-600">{error}</p>
              </div>
            )}

            {/* Invoices Table */}
            {!isLoading && !error && (
              <div className="overflow-x-auto rounded-3xl border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full border-collapse text-sm md:text-base">
                  <thead>
                    <tr className="bg-gray-50 text-left text-gray-600">
                      <th className="px-4 py-3 font-semibold">S.No</th>
                      <th className="px-4 py-3 font-semibold min-w-[180px]">Invoice#</th>
                      <th className="px-4 py-3 font-semibold min-w-[220px]">Customer</th>
                      <th className="px-4 py-3 font-semibold min-w-[180px]">Site</th>
                      <th className="px-4 py-3 font-semibold min-w-[100px]">Version</th>
                      <th className="px-4 py-3 font-semibold min-w-[140px]">Date</th>
                      <th className="px-4 py-3 font-semibold min-w-[160px]">PO Number</th>
                      <th className="px-4 py-3 font-semibold min-w-[160px]">Grand Total</th>
                      <th className="px-4 py-3 font-semibold min-w-[140px]">Status</th>
                      <th className="px-4 py-3 font-semibold min-w-[200px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map((invoice, index) => {
                      const siteName = getDisplaySiteName(invoice, sites);
                      const rowVersion = invoice.version_no ?? invoice.versionNo;
                      return (
                        <tr
                          key={invoice.id || invoice.fdoc_id || `inv-row-${index}`}
                          className="border-t border-gray-100 hover:bg-gray-50/60 cursor-pointer"
                          onClick={() => {
                            const isFdocInvoice = fetchFromFdoc || Boolean(invoice?.fdoc_id);
                            if (hideSelectors && invoice.status === 'Sent' && !isFdocInvoice) {
                              navigate('/onboarding-create-invoice', {
                                state: {
                                  fromFdoc: true,
                                  invoice,
                                  fdocType,
                                  fdocSite: externalSite ?? selectedSite,
                                  fdocMonth: selectedMonth,
                                },
                              });
                            } else {
                              setSelectedCard(invoice);
                            }
                          }}
                        >
                          <td className="px-4 py-4 align-top text-gray-800">{index + 1}</td>
                          <td className="px-4 py-4 align-top text-gray-800">{invoice.invoiceNumber || '—'}</td>
                          <td className="px-4 py-4 align-top text-gray-700">
                            <div className="max-w-[320px] truncate" title={invoice.client || ''}>
                              {invoice.client || '—'}
                            </div>
                          </td>
                          <td className="px-4 py-4 align-top text-gray-700">
                            <div className="max-w-[260px] truncate" title={siteName || ''}>
                              {siteName || '—'}
                            </div>
                          </td>
                          <td className="px-4 py-4 align-top text-gray-800">
                            {rowVersion != null && rowVersion !== '' ? `v${rowVersion}` : '—'}
                          </td>
                          <td className="px-4 py-4 align-top text-gray-700">{invoice.date || '—'}</td>
                          <td className="px-4 py-4 align-top text-gray-700">{invoice.poNumber || '—'}</td>
                          <td className="px-4 py-4 align-top text-gray-800">
                            ₹{(invoice.grandTotal ?? 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-4 align-top">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getStatusColor(invoice.status)}`}>
                              {invoice.status || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-4 align-top">
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(invoice);
                                }}
                                className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-800"
                                title="Download"
                              >
                                <Download className="h-4 w-4 text-white stroke-white" strokeWidth={2} />
                                Download
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!isLoading && !error && filteredInvoices.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">
                  {!selectedSite
                    ? 'Please select a site to view invoices.'
                    : 'No invoices found matching the selected criteria.'}
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Detail Modal */}
      {selectedCard &&
        createPortal(
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex flex-col bg-black/70 min-h-screen w-full"
            >
              <div className="flex-shrink-0 px-4 pt-6 pb-4">
                <div className="mx-auto flex w-full max-w-5xl flex-wrap items-center justify-between gap-3">
                  <div className="text-sm font-semibold uppercase tracking-widest text-white/80">
                    {(selectedCard?.fdoc_id || fetchFromFdoc) ? 'Document' : 'Invoice Details'}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCard(null);
                        setDownloadUrl(null);
                        setDownloadUrlExpiresIn(null);
                        setDocumentContentType(null);
                      }}
                      className="rounded-full border border-white/40 bg-white/15 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/25"
                    >
                      Close
                    </button>
                    {!(selectedCard?.fdoc_id || fetchFromFdoc) && (
                      <button
                        type="button"
                        onClick={() => handleDownload(selectedCard)}
                        className="rounded-full bg-emerald-500 px-6 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-emerald-600"
                      >
                        <span className="inline-flex items-center gap-2">
                          <Download className="w-4 h-4 text-white stroke-white" strokeWidth={2} />
                          Download PDF
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 pb-6">
                <motion.div
                  variants={modalVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  className="mx-auto w-full max-w-5xl rounded-[32px] bg-white p-6 text-gray-800 shadow-2xl sm:p-8"
                >
                  {isLoadingDetails ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500">Loading...</p>
                    </div>
                  ) : (
                    <>
                      {/* For OO (FDOC) invoices: Only show PDF document, no preview */}
                      {(selectedCard?.fdoc_id || fetchFromFdoc) ? (
                        <>
                          {downloadUrl ? (
                            <div className="w-full h-full min-h-[80vh]">
                              {documentContentType && documentContentType.startsWith('image/') ? (
                                // Display images using img tag
                                <div className="flex justify-center items-center w-full h-full min-h-[80vh] p-4">
                                  <img
                                    src={downloadUrl}
                                    alt="Invoice Document"
                                    className="max-w-full max-h-full object-contain rounded-lg"
                                  />
                                </div>
                              ) : (
                                // Display PDFs using iframe - full screen
                                <div className="w-full h-full min-h-[80vh]">
                                  <iframe
                                    src={downloadUrl}
                                    title="Invoice Document"
                                    className="w-full h-full min-h-[80vh] border-0"
                                    onError={() => {
                                      console.error('Failed to load PDF document. URL may be invalid or expired.');
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-12">
                              <div className="max-w-md mx-auto">
                                <p className="text-gray-500 mb-2">No document available or failed to load.</p>
                                {downloadUrl && (
                                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                    <p className="text-sm font-semibold text-yellow-800 mb-1">
                                      Document URL Error
                                    </p>
                                    <p className="text-xs text-yellow-700">
                                      The document URL could not be loaded. This is likely due to a backend configuration issue where presigned URLs are generated with the wrong AWS region (ap-south-1 instead of us-east-1).
                                    </p>
                                    <p className="text-xs text-yellow-700 mt-2">
                                      Please contact support or check backend logs for region mismatch errors.
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {/* For non-OO invoices: Show invoice details, no document viewer */}
                          <div className="flex items-start justify-between mb-6">
                            <div>
                              <h2 className="text-2xl font-semibold text-gray-900">{selectedCard.invoiceNumber}</h2>
                              <p className="text-gray-500 mt-1">{selectedCard.client}</p>
                            </div>
                          </div>

                          <div className="grid gap-6 md:grid-cols-2 mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Information</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Date:</span>
                      <span className="font-medium text-gray-700">{selectedCard.date}</span>
                    </div>
                    {selectedCard.dueDate && (
                    <div className="flex justify-between">
                        <span className="text-gray-500">Due Date:</span>
                        <span className="font-medium text-gray-700">{selectedCard.dueDate}</span>
                    </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">Payment Terms:</span>
                      <span className="font-medium text-gray-700">{selectedCard.paymentTerms}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Payment Status:</span>
                      <span className="font-medium text-gray-700 capitalize">{selectedCard.paymentStatus || 'unpaid'}</span>
                    </div>
                    {selectedCard.poNumber && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">PO Number:</span>
                      <span className="font-medium text-gray-700">{selectedCard.poNumber}</span>
                    </div>
                    )}
                    {selectedCard.paymentMode && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Payment Mode:</span>
                      <span className="font-medium text-gray-700">{selectedCard.paymentMode}</span>
                    </div>
                    )}
                    {selectedCard.modeOfTransaction && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Mode of transaction:</span>
                      <span className="font-medium text-gray-700">{selectedCard.modeOfTransaction}</span>
                    </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">Site:</span>
                      <span className="font-medium text-gray-700">{getDisplaySiteName(selectedCard, sites)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Currency:</span>
                      <span className="font-medium text-gray-700">{selectedCard.currency || 'INR'}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Addresses</h3>
                  <div className="space-y-3 text-sm text-gray-700">
                    <div>
                      <p className="font-semibold text-gray-600 mb-1">Billing Address:</p>
                      <p>{selectedCard.billingAddress}</p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-600 mb-1">Shipping Address:</p>
                      <p>{selectedCard.shippingAddress}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment and transaction details</h3>
                <div className="space-y-2 text-sm rounded-xl border border-gray-200 p-4 bg-gray-50/80">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getStatusColor(selectedCard.status)}`}>
                      {selectedCard.status || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Transaction ID</span>
                    <span className="font-medium text-gray-700">{selectedCard.transactionId || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Amount paid</span>
                    <span className="font-medium text-gray-700">
                      {selectedCard.amountPaid != null && selectedCard.amountPaid !== ''
                        ? (typeof selectedCard.amountPaid === 'number'
                            ? `₹${Number(selectedCard.amountPaid).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : selectedCard.amountPaid)
                        : '—'}
                    </span>
                  </div>
                  {selectedCard.paymentMode && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Payment mode</span>
                      <span className="font-medium text-gray-700">{selectedCard.paymentMode}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Line Items</h3>
                <div className="overflow-x-auto rounded-2xl border border-gray-200">
                  <table className="min-w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left text-gray-600">
                        <th className="px-4 py-3 font-semibold">S.No</th>
                        <th className="px-4 py-3 font-semibold">Name</th>
                        <th className="px-4 py-3 font-semibold">Quantity</th>
                        <th className="px-4 py-3 font-semibold">Units</th>
                        <th className="px-4 py-3 font-semibold">Rate</th>
                        <th className="px-4 py-3 font-semibold text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCard.lineItems.map((item, index) => (
                        <tr key={index} className="border-t border-gray-100">
                          <td className="px-4 py-3">{index + 1}</td>
                          <td className="px-4 py-3">{item.name}</td>
                          <td className="px-4 py-3">{item.quantity}</td>
                          <td className="px-4 py-3">{item.units}</td>
                          <td className="px-4 py-3">₹{item.rate.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right">₹{item.amount.toFixed(2)}</td>
                        </tr>
                      ))}
                      <tr className="border-t border-gray-200 bg-gray-50 text-gray-700">
                        <td colSpan={5} className="px-4 py-3 font-semibold">Subtotal</td>
                        <td className="px-4 py-3 text-right font-semibold">₹{selectedCard.subtotal.toFixed(2)}</td>
                      </tr>
                      {selectedCard.discount > 0 && (
                        <tr className="border-t border-gray-100 bg-white text-gray-700">
                          <td colSpan={5} className="px-4 py-3 font-semibold">Discount</td>
                          <td className="px-4 py-3 text-right font-semibold">₹{selectedCard.discount.toFixed(2)}</td>
                        </tr>
                      )}
                      {selectedCard.shippingHandling > 0 && (
                        <tr className="border-t border-gray-100 bg-white text-gray-700">
                          <td colSpan={5} className="px-4 py-3 font-semibold">Shipping / Handling</td>
                          <td className="px-4 py-3 text-right font-semibold">₹{selectedCard.shippingHandling.toFixed(2)}</td>
                        </tr>
                      )}
                      {selectedCard.insurance > 0 && (
                        <tr className="border-t border-gray-100 bg-white text-gray-700">
                          <td colSpan={5} className="px-4 py-3 font-semibold">Insurance</td>
                          <td className="px-4 py-3 text-right font-semibold">₹{selectedCard.insurance.toFixed(2)}</td>
                        </tr>
                      )}
                      <tr className="border-t border-gray-100 bg-white text-gray-700">
                        <td colSpan={5} className="px-4 py-3 font-semibold">Total Tax</td>
                        <td className="px-4 py-3 text-right font-semibold">₹{selectedCard.totalTax.toFixed(2)}</td>
                      </tr>
                      <tr className="border-t border-gray-200 bg-gray-100 text-gray-900 font-semibold">
                        <td colSpan={5} className="px-4 py-3">Grand Total</td>
                        <td className="px-4 py-3 text-right">₹{selectedCard.grandTotal.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Special Notes & Terms of Sale</h3>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {(() => {
                    const { specialNotes, termsOfSale } = splitNotesAndTerms(selectedCard.notes || '', selectedCard.termsOfSale ?? '');
                    return (
                      <>
                        <div className="rounded-xl border-2 border-gray-200 bg-white p-4 shadow-sm">
                          <h4 className="text-sm font-semibold text-gray-800 mb-2 border-b border-gray-100 pb-2">Special Notes</h4>
                          <p className="text-sm text-gray-700 whitespace-pre-line min-h-[4rem]">{specialNotes || '—'}</p>
                        </div>
                        <div className="rounded-xl border-2 border-gray-200 bg-white p-4 shadow-sm">
                          <h4 className="text-sm font-semibold text-gray-800 mb-2 border-b border-gray-100 pb-2">Terms of Sale</h4>
                          <p className="text-sm text-gray-700 whitespace-pre-line min-h-[4rem]">{termsOfSale || '—'}</p>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
                        </>
                      )}
                    </>
                  )}
                </motion.div>
              </div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )}

    </div>
  );
};

export default InvoiceView;

